import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import type { AnalyticsFunnel } from '@lib/analytics/events';

const FUNNEL_STARTED_SESSION_KEY_PREFIX = 'ffh_funnel_started_';

/**
 * FFH-2372 — `funnel_started`, once per session per funnel.
 *
 * The flow controllers remount on refresh, and their refresh guard bounces
 * a mid-funnel reload back to the first step; neither is a new start, so a
 * bare mount-time fire would over-count. A sessionStorage latch (the
 * fireLead pattern) keeps it to one start per funnel per browsing session.
 */
export const captureFunnelStarted = (
  funnel: AnalyticsFunnel,
  entryRoute: string
): void => {
  if (typeof window === 'undefined') return;

  try {
    const sessionKey = FUNNEL_STARTED_SESSION_KEY_PREFIX + funnel;
    if (window.sessionStorage.getItem(sessionKey) === 'true') return;
    window.sessionStorage.setItem(sessionKey, 'true');
  } catch {
    // Storage unavailable (private browsing, blocked cookies): accept a
    // possible duplicate rather than dropping the start.
  }

  captureAnalyticsEvent(AnalyticsEvents.funnelStarted, {
    funnel,
    entry_route: entryRoute
  });
};

/**
 * FFH-2372 — `funnel_step_completed`, fired by the controllers when a step
 * advances. `stepIndex` is the progress-pip index (what the user sees on the
 * top bar) and `stepName` the code-owned progress group id, so a Sanity edit
 * cannot silently rename steps in the funnel insight.
 */
export const captureFunnelStepCompleted = (
  funnel: AnalyticsFunnel,
  stepIndex: number,
  stepName: string
): void => {
  captureAnalyticsEvent(AnalyticsEvents.funnelStepCompleted, {
    funnel,
    step_index: stepIndex,
    step_name: stepName
  });
};

/** FFH-2372 — `funnel_email_captured`. Deliberately carries no email: no PII. */
export const captureFunnelEmailCaptured = (funnel: AnalyticsFunnel): void => {
  captureAnalyticsEvent(AnalyticsEvents.funnelEmailCaptured, { funnel });
};
